import { useCallback, useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import toast from 'react-hot-toast';
import {
  AlignLeft, Calendar, CalendarClock, Coins, Edit2, Hash, Link2, List, ListChecks, Mail, Phone, Plus, Settings2,
  ToggleLeft, Trash2, Type, type LucideIcon,
} from 'lucide-react';
import { Button } from '@/components/Button';
import { Input } from '@/components/Input';
import { Select } from '@/components/Select';
import { Textarea } from '@/components/Textarea';
import { FormField } from '@/components/FormField';
import { Modal } from '@/components/Modal';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { LoadErrorState } from '@/components/ListStates';
import { SettingsCard, SettingsPage, SettingsSection, Switch } from '@/components/settings/SettingsPage';
import { useAuth } from '@/hooks/useAuth';
import {
  customFieldsService, CustomFieldDefinition, CustomFieldEntityType, CustomFieldKind,
} from '@/services/custom-fields.service';

const ENTITY_TABS: { value: CustomFieldEntityType; label: string }[] = [
  { value: 'contact', label: 'Contacts' },
  { value: 'lead',    label: 'Leads' },
  { value: 'deal',    label: 'Deals' },
];

const KINDS: { value: CustomFieldKind; label: string; icon: LucideIcon }[] = [
  { value: 'text',         label: 'Short text',        icon: Type },
  { value: 'long_text',    label: 'Long text',         icon: AlignLeft },
  { value: 'number',       label: 'Number',            icon: Hash },
  { value: 'currency',     label: 'Currency',          icon: Coins },
  { value: 'date',         label: 'Date',              icon: Calendar },
  { value: 'datetime',     label: 'Date & time',       icon: CalendarClock },
  { value: 'dropdown',     label: 'Dropdown (single)', icon: List },
  { value: 'multi_select', label: 'Dropdown (multi)',  icon: ListChecks },
  { value: 'boolean',      label: 'Yes/No',            icon: ToggleLeft },
  { value: 'url',          label: 'URL',               icon: Link2 },
  { value: 'email',        label: 'Email',             icon: Mail },
  { value: 'phone',        label: 'Phone',             icon: Phone },
];

const KIND_BY_VALUE = Object.fromEntries(KINDS.map((k) => [k.value, k])) as Record<CustomFieldKind, (typeof KINDS)[number]>;

function needsOptions(kind: CustomFieldKind): boolean {
  return kind === 'dropdown' || kind === 'multi_select';
}

interface FormState {
  name: string;
  kind: CustomFieldKind;
  required: boolean;
  helpText: string;
  placeholder: string;
  defaultValue: string;
  options: string;  // newline-separated in the form, split before submit
}

const emptyForm: FormState = {
  name: '', kind: 'text', required: false, helpText: '', placeholder: '', defaultValue: '', options: '',
};

export default function CustomFieldsSettings() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'company_admin';
  const [entityType, setEntityType] = useState<CustomFieldEntityType>('contact');
  const [fields, setFields] = useState<CustomFieldDefinition[] | null>(null);
  const [loadError, setLoadError] = useState(false);
  const [editing, setEditing] = useState<CustomFieldDefinition | null>(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [busy, setBusy] = useState(false);
  const [del, setDel] = useState<CustomFieldDefinition | null>(null);

  // A slow answer for the tab you just left must not replace the list of the tab you are on.
  const shownEntity = useRef(entityType);
  const load = useCallback(() => {
    setLoadError(false);
    return customFieldsService.list(entityType)
      .then((list) => { if (shownEntity.current === entityType) setFields(list); })
      .catch(() => { if (shownEntity.current === entityType) setLoadError(true); });
  }, [entityType]);

  useEffect(() => { shownEntity.current = entityType; setFields(null); void load(); }, [entityType, load]);

  const startCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setOpen(true);
  };

  const startEdit = (f: CustomFieldDefinition) => {
    setEditing(f);
    setForm({
      name: f.name,
      kind: f.kind,
      required: f.required,
      helpText: f.helpText ?? '',
      placeholder: f.placeholder ?? '',
      defaultValue: f.defaultValue ?? '',
      options: (f.options ?? []).join('\n'),
    });
    setOpen(true);
  };

  const submit = async () => {
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    if (needsOptions(form.kind) && !form.options.trim()) {
      toast.error('Dropdown fields need at least one option');
      return;
    }
    setBusy(true);
    try {
      const body = {
        name: form.name.trim(),
        kind: form.kind,
        required: form.required,
        helpText: form.helpText.trim() || undefined,
        placeholder: form.placeholder.trim() || undefined,
        defaultValue: form.defaultValue.trim() || undefined,
        options: needsOptions(form.kind)
          ? form.options.split('\n').map((s) => s.trim()).filter(Boolean)
          : undefined,
      };
      if (editing) {
        // The type is fixed after creation (the API refuses `kind` on update); '' clears an optional text.
        const { kind: _kind, ...rest } = body;
        await customFieldsService.update(editing.fieldUid, {
          ...rest, helpText: form.helpText.trim(), placeholder: form.placeholder.trim(), defaultValue: form.defaultValue.trim(),
        });
        toast.success('Field updated');
      } else {
        await customFieldsService.create(entityType, body);
        toast.success('Field created');
      }
      setOpen(false);
      void load();
    } catch {
      // axios interceptor already toasted backend message
    } finally { setBusy(false); }
  };

  const toggleActive = async (f: CustomFieldDefinition) => {
    const setActive = (isActive: boolean) =>
      setFields((cur) => cur && cur.map((x) => (x.fieldUid === f.fieldUid ? { ...x, isActive } : x)));
    const next = !f.isActive;
    setActive(next);
    try {
      await customFieldsService.update(f.fieldUid, { isActive: next });
      void load();
    } catch (e) {
      setActive(f.isActive);
      if (!(e as { response?: unknown })?.response) toast.error('Failed to update field');
    }
  };

  const remove = async () => {
    if (!del) return;
    setBusy(true);
    try {
      await customFieldsService.remove(del.fieldUid);
      toast.success('Field deleted');
      setDel(null);
      void load();
    } catch {
      // axios interceptor already toasted backend message
    } finally { setBusy(false); }
  };

  const tabLabel = ENTITY_TABS.find((t) => t.value === entityType)?.label.toLowerCase() ?? 'records';

  return (
    <>
      <SettingsPage
        eyebrow="Workspace"
        title="Custom fields"
        description="Add your own fields to contacts, leads and deals, so you can keep the details your business needs."
        actions={isAdmin && <Button iconLeft={<Plus size={14} />} onClick={startCreate}>New field</Button>}
      >
        <SettingsSection
          title="Fields"
          description={isAdmin
            ? 'Fields show on the matching forms automatically. Switch one off to hide it without losing what was saved.'
            : 'You have view-only access. Ask a company admin to make changes.'}
        >
          <SettingsCard padded={false}>
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-5 py-3">
              <div role="group" aria-label="Record type" className="inline-flex gap-0.5 rounded-lg border border-border bg-surface-alt p-0.5">
                {ENTITY_TABS.map((t) => {
                  const active = entityType === t.value;
                  return (
                    <button
                      key={t.value}
                      type="button"
                      aria-pressed={active}
                      onClick={() => setEntityType(t.value)}
                      className={clsx(
                        'rounded-md px-3 py-1.5 text-xs font-medium transition-all',
                        active ? 'bg-surface text-text-primary shadow-soft' : 'text-text-muted hover:text-text-primary',
                      )}
                    >
                      {t.label}
                    </button>
                  );
                })}
              </div>
              {fields && <span className="text-2xs tabular-nums text-text-muted">{fields.length} field{fields.length === 1 ? '' : 's'}</span>}
            </div>

            {fields === null && loadError ? (
              <LoadErrorState bare what="your custom fields" onRetry={() => void load()} />
            ) : fields === null ? (
              <div className="space-y-2 p-5">{[0, 1, 2].map((i) => <div key={i} className="skeleton h-14 rounded-xl" />)}</div>
            ) : fields.length === 0 ? (
              <div className="flex flex-col items-center gap-3 px-6 py-12 text-center">
                <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-primary text-white shadow-soft"><Settings2 size={20} /></div>
                <div className="text-sm font-medium text-text-primary">No custom fields on {tabLabel} yet</div>
                {isAdmin && <Button size="sm" variant="secondary" iconLeft={<Plus size={13} />} onClick={startCreate}>Add the first field</Button>}
              </div>
            ) : (
              <ul className="divide-y divide-border">
                {fields.map((f) => {
                  const KindIcon = KIND_BY_VALUE[f.kind]?.icon ?? Type;
                  const optionCount = f.options?.length ?? 0;
                  return (
                    <li key={f.fieldUid} className="group flex items-center gap-3 px-5 py-3.5 transition-colors hover:bg-surface-alt/60 @md:gap-4">
                      <div className={clsx('flex min-w-0 flex-1 items-center gap-3 @md:gap-4', !f.isActive && 'opacity-60')}>
                        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gold-gradient-soft text-primary"><KindIcon size={17} /></span>
                        <div className="min-w-0 flex-1">
                          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                            <span className="truncate text-sm font-semibold text-text-primary">{f.name}</span>
                            <span className="rounded-full bg-surface-alt px-2 py-0.5 text-2xs text-text-muted">{KIND_BY_VALUE[f.kind]?.label ?? f.kind}</span>
                            {f.required && <span className="rounded-full bg-primary-soft px-2 py-0.5 text-2xs font-semibold text-primary">Required</span>}
                            {!f.isActive && <span className="rounded-full bg-surface-alt px-2 py-0.5 text-2xs font-semibold text-text-muted">Off</span>}
                          </div>
                          <div className="mt-0.5 flex flex-wrap items-center gap-x-2 text-2xs text-text-muted">
                            <span className="min-w-0 max-w-full truncate font-mono">{f.key}</span>
                            {optionCount > 0 && <span>· {optionCount} option{optionCount === 1 ? '' : 's'}</span>}
                            {f.helpText && <span className="min-w-0 truncate">· {f.helpText}</span>}
                          </div>
                        </div>
                      </div>

                      {isAdmin && (
                        <div className="flex shrink-0 items-center gap-2">
                          <div className="flex items-center gap-0.5 md:opacity-0 md:transition-opacity md:group-hover:opacity-100 md:group-focus-within:opacity-100">
                            <button type="button" onClick={() => startEdit(f)} className="flex h-8 w-8 items-center justify-center rounded-lg text-text-muted transition-colors hover:bg-surface-alt hover:text-text-primary" aria-label={`Edit ${f.name}`} title="Edit"><Edit2 size={14} /></button>
                            <button type="button" onClick={() => setDel(f)} className="flex h-8 w-8 items-center justify-center rounded-lg text-text-muted transition-colors hover:bg-danger-soft hover:text-danger" aria-label={`Delete ${f.name}`} title="Delete"><Trash2 size={14} /></button>
                          </div>
                          <Switch checked={f.isActive} onChange={() => toggleActive(f)} label={`${f.name} is ${f.isActive ? 'on' : 'off'}`} />
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </SettingsCard>
        </SettingsSection>
      </SettingsPage>

      {/* Outside SettingsPage: its container box would become the containing block of these fixed overlays. They sit in no container, so their grids use viewport breakpoints. */}
      <Modal open={open} onClose={() => !busy && setOpen(false)} title={editing ? `Edit "${editing.name}"` : 'New custom field'}>
        <div className="grid gap-x-4 sm:grid-cols-2">
          <FormField label="Field name" required hintBelow hint={editing ? 'The name can’t change after the field is created.' : 'What people see on the form, for example VAT number.'}>
            <Input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              disabled={!!editing}
              placeholder="VAT number"
            />
          </FormField>

          <FormField label="Type" required hintBelow hint={editing ? 'The type can’t change after the field is created.' : undefined}>
            <Select
              value={form.kind}
              onChange={(e) => setForm({ ...form, kind: e.target.value as CustomFieldKind })}
              disabled={!!editing}
            >
              {KINDS.map((k) => <option key={k.value} value={k.value}>{k.label}</option>)}
            </Select>
          </FormField>
        </div>

        {needsOptions(form.kind) && (
          <FormField label="Options" required hintBelow hint="One option per line.">
            <Textarea
              rows={5}
              value={form.options}
              onChange={(e) => setForm({ ...form, options: e.target.value })}
              placeholder={'Hot\nWarm\nCold'}
            />
          </FormField>
        )}

        <FormField label="Help text" hintBelow hint="Shown under the field on forms.">
          <Input
            value={form.helpText}
            onChange={(e) => setForm({ ...form, helpText: e.target.value })}
            placeholder="Optional explanation"
          />
        </FormField>

        <div className="grid gap-x-4 sm:grid-cols-2">
          <FormField label="Placeholder" hintBelow hint="Grey text inside the empty input.">
            <Input
              value={form.placeholder}
              onChange={(e) => setForm({ ...form, placeholder: e.target.value })}
              placeholder="Optional"
            />
          </FormField>

          <FormField label="Default value" hintBelow hint="Filled in until someone changes it.">
            <Input
              value={form.defaultValue}
              onChange={(e) => setForm({ ...form, defaultValue: e.target.value })}
            />
          </FormField>
        </div>

        <div className="flex items-center gap-4 rounded-xl border border-border bg-surface-alt/50 px-4 py-3">
          <div className="min-w-0 flex-1">
            <div className="text-sm font-semibold text-text-primary">Required field</div>
            <div className="mt-0.5 text-xs text-text-muted">Marks the field with a * on forms.</div>
          </div>
          <Switch checked={form.required} onChange={(next) => setForm({ ...form, required: next })} label="Required field" />
        </div>

        <div className="mt-5 flex justify-end gap-2">
          <Button variant="secondary" onClick={() => setOpen(false)} disabled={busy}>Cancel</Button>
          <Button onClick={submit} loading={busy}>{editing ? 'Save' : 'Create field'}</Button>
        </div>
      </Modal>

      <ConfirmDialog
        open={!!del}
        title="Delete custom field?"
        message={del ? `Delete "${del.name}"? Existing values stored on records will remain in the database but won't display anywhere.` : ''}
        danger
        onConfirm={remove}
        onCancel={() => setDel(null)}
      />
    </>
  );
}
